import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import "./contact-search.scss";
import ContactHelper from "../../../../entities/contact/contact-helper";
import { EventEmitter } from "../../../../shared/utils/event-emitter";
import Events from "../../../../const/events";
import ContactApi from "../../../../api/contact-api";

const ContactSearch = ({ onContactSelect, onAddNewContact, placeholder = "Search by name, phone, or email...", disabled = false }) => {
  const [searchText, setSearchText] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isContactSelected, setIsContactSelected] = useState(false);
  const { contacts } = useSelector((state) => state.contactsStore);

  // Debounced search function
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  // Function to fetch contacts from backend with search query
  const fetchContacts = async (query) => {
    if (!query || query.trim() === "" || isContactSelected) {
      setFilteredContacts([]);
      setShowResults(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const { data } = await ContactApi.private.getContacts({ search: query });
      if (data?.data?.docs) {
        setFilteredContacts(data.data.docs);
      } else {
        setFilteredContacts([]);
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
      setFilteredContacts([]);
    } finally {
      setIsLoading(false);
      setShowResults(true);
    }
  };

  // Create debounced search function
  const debouncedFetchContacts = useCallback(
    debounce((query) => fetchContacts(query), 500),
    [isContactSelected]
  );

  // Update search results when search text changes
  useEffect(() => {
    debouncedFetchContacts(searchText);
  }, [searchText, debouncedFetchContacts]);

  const handleContactSelect = (contact) => {
    setIsContactSelected(true);
    onContactSelect(contact);
    setSearchText(contact.name || contact.phone || contact.email);
    setShowResults(false);
  };

  const handleInputChange = (e) => {
    setIsContactSelected(false);
    setSearchText(e.target.value);
  };

  const handleAddNewContact = () => {
    // Call the direct callback if provided
    if (onAddNewContact) {
      onAddNewContact(searchText);
    } else {
      // Otherwise fall back to the event system
      EventEmitter.dispatch(Events.SHOW_ADD_CONTACT_MODAL, {
        initialName: searchText
      });
    }
    setShowResults(false);
  };

  return (
    <div className={`contact-search ${disabled ? 'disabled' : ''}`}>
      <div className="search-input-wrapper">
        <input
          type="text"
          className="form-control"
          placeholder={placeholder}
          value={searchText}
          onChange={handleInputChange}
          onFocus={() => {
            if (!isContactSelected && searchText && !disabled) {
              setShowResults(true);
            }
          }}
          disabled={disabled}
        />
        {showResults && !disabled && (
          <div className="search-results">
            {isLoading ? (
              <div className="loading">
                <img src="assets/images/ring-loader.svg" alt="loader" className="loader" />
                <span>Loading contacts...</span>
              </div>
            ) : filteredContacts.length > 0 ? (
              filteredContacts.map((contact) => (
                <div
                  key={contact._id}
                  className="contact-item"
                  onClick={() => handleContactSelect(contact)}
                >
                  <div className="contact-name">{contact.name || "Unknown"}</div>
                  <div className="contact-details">
                    {contact.phone && (
                      <span className="contact-chip phone-chip">
                        {contact.phone}
                      </span>
                    )}
                    {contact.email && (
                      <span className="contact-chip email-chip">
                        {contact.email}
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-results">
                No contacts found. <br />
                <button
                  className="btn btn-link btn-sm p-0"
                  onClick={handleAddNewContact}
                >
                  Add new contact
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactSearch; 